import { useMemo, useState } from 'react';

const DAYS_AHEAD = 28;
const STATUS_COL = {
  Pending: { bg: '#FEF3C7', tx: '#92400E', bar: '#F59E0B' },
  Confirmed: { bg: '#DBEAFE', tx: '#1E40AF', bar: '#3B82F6' },
  Completed: { bg: '#DCFCE7', tx: '#166534', bar: '#10B981' },
  Cancelled: { bg: '#F1F5F9', tx: '#64748B', bar: '#94A3B8' },
};

const VEHICLE_COL_W = 220;
const DAY_W = 38;

function fmtDay(d) { return d.toISOString().slice(0, 10); }
function dayLabel(d) { return d.toLocaleDateString('en-US', { day: '2-digit' }); }
function monthLabel(d) { return d.toLocaleDateString('en-US', { month: 'short' }); }
function dowLabel(d) { return d.toLocaleDateString('en-US', { weekday: 'short' })[0]; }

function diffDays(a, b) {
  return Math.round((new Date(a) - new Date(b)) / 86400000);
}

export default function BookingsCalendar({ bookings, fleet, onPick }) {
  const [startOffset, setStartOffset] = useState(0);
  const [showCancelled, setShowCancelled] = useState(false);

  const days = useMemo(() => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + startOffset);
    return Array.from({ length: DAYS_AHEAD }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d;
    });
  }, [startOffset]);

  const startDay = fmtDay(days[0]);
  const endDay = fmtDay(days[days.length - 1]);
  const today = fmtDay(new Date());

  const rows = useMemo(() => fleet.map((car) => ({
    car,
    bookings: bookings.filter(
      (b) =>
        b.vid === car.id &&
        (showCancelled || b.status !== 'Cancelled') &&
        b.pickup_date <= endDay &&
        b.dropoff_date >= startDay
    ),
  })), [bookings, fleet, startDay, endDay, showCancelled]);

  const totalVisible = rows.reduce((s, r) => s + r.bookings.length, 0);
  const innerWidth = DAYS_AHEAD * DAY_W;

  return (
    <div data-testid="bookings-calendar">
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button data-testid="cal-prev" onClick={() => setStartOffset((o) => o - 7)} style={btnSecondary}>← Prev week</button>
        <button data-testid="cal-today" onClick={() => setStartOffset(0)} style={{ ...btnSecondary, fontWeight: 800 }}>Today</button>
        <button data-testid="cal-next" onClick={() => setStartOffset((o) => o + 7)} style={btnSecondary}>Next week →</button>
        <span style={{ marginLeft: 6, color: '#64748B', fontSize: 13, fontWeight: 600 }}>
          {days[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} —{' '}
          {days[DAYS_AHEAD - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748B', fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} style={{ accentColor: '#0D1B3E' }} />
          Show cancelled
        </label>
        <Legend />
      </div>

      {/* Grid (custom row layout — vehicle column + scrollable day strip) */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
        <div style={{ display: 'flex', minWidth: VEHICLE_COL_W + innerWidth }}>
          {/* Left sticky vehicle column */}
          <div style={{ width: VEHICLE_COL_W, flexShrink: 0, borderRight: '1px solid #E2E8F0', background: '#fff' }}>
            <div style={{ ...headerCell, height: 52, background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 14, color: '#0F172A' }}>Vehicle</div>
            {rows.map(({ car }) => (
              <div key={car.id} style={{ height: 52, borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px' }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg,${car.color}22,${car.color}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, overflow: 'hidden', flexShrink: 0 }}>
                  {car.img ? (
                    <img src={car.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  ) : null}
                  <span style={{ display: car.img ? 'none' : 'block' }}>{car.emoji}</span>
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{car.brand} {car.model}</div>
                  <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>{car.plate || car.id.toUpperCase()}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Right day-strip */}
          <div style={{ width: innerWidth, flexShrink: 0 }}>
            {/* Header row */}
            <div style={{ display: 'flex', height: 52, background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              {days.map((d, i) => {
                const isToday = fmtDay(d) === today;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isMonthStart = d.getDate() === 1 || i === 0;
                return (
                  <div key={i} style={{ width: DAY_W, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #F1F5F9', background: isToday ? '#FEF3C7' : isWeekend ? '#F1F5F9' : 'transparent' }}>
                    {isMonthStart && <div style={{ fontSize: 9, color: '#64748B', fontWeight: 800, textTransform: 'uppercase' }}>{monthLabel(d)}</div>}
                    <div style={{ fontSize: 12, fontWeight: 800, color: isToday ? '#92400E' : '#0F172A' }}>{dayLabel(d)}</div>
                    <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700 }}>{dowLabel(d)}</div>
                  </div>
                );
              })}
            </div>

            {/* Vehicle rows */}
            {rows.map(({ car, bookings: bks }) => (
              <div key={car.id} style={{ position: 'relative', height: 52, borderTop: '1px solid #E2E8F0', display: 'flex' }}>
                {days.map((d, i) => {
                  const isToday = fmtDay(d) === today;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div key={i} style={{ width: DAY_W, flexShrink: 0, borderRight: '1px solid #F1F5F9', background: isToday ? '#FFFBEB' : isWeekend ? '#F8FAFC' : '#fff' }} />
                  );
                })}
                {/* Booking blocks overlaid */}
                {bks.map((b) => {
                  const startIdx = Math.max(0, diffDays(b.pickup_date, startDay));
                  const endIdx = Math.min(DAYS_AHEAD, diffDays(b.dropoff_date, startDay));
                  const span = Math.max(1, endIdx - startIdx);
                  const col = STATUS_COL[b.status] || STATUS_COL.Pending;
                  return (
                    <button
                      key={b.id}
                      data-testid={`cal-booking-${b.id}`}
                      onClick={() => onPick?.(b)}
                      title={`${b.customer_name} · ${b.pickup_date} → ${b.dropoff_date} · ${b.status}${b.payment_status === 'paid' ? ' · PAID' : ''}`}
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: startIdx * DAY_W + 2,
                        width: span * DAY_W - 4,
                        height: 36,
                        background: col.bg,
                        color: col.tx,
                        border: `1.5px solid ${col.bar}`,
                        borderLeft: `4px solid ${col.bar}`,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        textAlign: 'left',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        padding: '0 8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {b.customer_name}
                      {b.payment_status === 'paid' && <span style={{ marginLeft: 4 }}>💳</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {fleet.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>No vehicles in fleet.</div>
        )}
      </div>

      <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 10 }}>
        {totalVisible} booking{totalVisible === 1 ? '' : 's'} in window. Click a block for details in the list view.
      </p>
    </div>
  );
}

const headerCell = {
  padding: '8px 4px',
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748B',
};

const btnSecondary = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1.5px solid #E2E8F0',
  background: '#fff',
  fontSize: 12,
  fontWeight: 700,
  color: '#0D1B3E',
  cursor: 'pointer',
};

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {['Pending', 'Confirmed', 'Completed'].map((s) => {
        const c = STATUS_COL[s];
        return (
          <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748B', fontWeight: 600 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `1.5px solid ${c.bar}`, borderLeft: `4px solid ${c.bar}` }} />
            {s}
          </span>
        );
      })}
    </div>
  );
}
